import {SubNav} from '@primer/react'
import {useMemo} from 'react'
import {type To, useLocation} from 'react-router-dom'

import type {MemexChartTimePeriod} from '../../../../api/charts/contracts/api'
import {LinkWithoutActiveClassName} from '../../../../components/react-router/link-without-active-classname'
import {boxShadowBorder} from '../../../../helpers/box-shadow-border'
import {END_DATE_PARAM, PERIOD_PARAM, START_DATE_PARAM} from '../../../../platform/url'

interface PeriodOption {
  value: MemexChartTimePeriod
  text?: string
}

const PERIOD_OPTIONS: Array<PeriodOption> = [{value: '2W'}, {value: '1M'}, {value: '3M'}, {text: 'Max', value: 'max'}]

function PeriodNavigationLink({
  periodValue,
  selected,
  children,
}: {
  periodValue: MemexChartTimePeriod
  selected: boolean
  children: string
}) {
  const location = useLocation()

  const to: To = useMemo(() => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.set(PERIOD_PARAM, periodValue)
    nextParams.delete(START_DATE_PARAM)
    nextParams.delete(END_DATE_PARAM)
    return {
      pathname: location.pathname,
      search: nextParams.toString(),
    }
  }, [periodValue, location.pathname, location.search])

  const conditionalStyles = selected
    ? {}
    : {
        '&:hover': {
          backgroundColor: 'transparent',
          boxShadow: (theme: FixMeTheme) =>
            `${boxShadowBorder({size: '1px', color: theme.colors.border.default})}, ${
              theme.shadows.primer.shadow.inset
            }`,
        },
      }

  return (
    <SubNav.Link
      key={periodValue}
      as={LinkWithoutActiveClassName}
      to={to}
      selected={selected}
      sx={{
        borderRadius: 6,
        px: '10px',
        fontWeight: 400,
        mr: '4px',
        minHeight: '30px',
        border: 'none',
        '&:first-of-type': {
          borderLeft: 'none',
        },
        ...conditionalStyles,
      }}
    >
      {children}
    </SubNav.Link>
  )
}

export function PeriodNavigation({period}: {period: MemexChartTimePeriod}) {
  return (
    <SubNav
      aria-label="Main"
      sx={{
        '> div:first-child': {
          marginBottom: 0,
        },
      }}
    >
      <SubNav.Links>
        {PERIOD_OPTIONS.map((periodValue, periodIndex) => {
          return (
            <PeriodNavigationLink
              key={periodValue.value}
              periodValue={periodValue.value}
              selected={period === periodValue.value || (!period && periodIndex === 0)}
            >
              {periodValue.text ?? periodValue.value}
            </PeriodNavigationLink>
          )
        })}
      </SubNav.Links>
    </SubNav>
  )
}

try{ PeriodNavigationLink.displayName ||= 'PeriodNavigationLink' } catch {}
try{ PeriodNavigation.displayName ||= 'PeriodNavigation' } catch {}